<script lang="ts">
	import { fly } from 'svelte/transition';
	import Spinner from './smol_ui/spinner.svelte';
	import { requestMagicLink } from '$lib/account.svelte.js';
	import { toastDisplay } from '$lib/utils.svelte.js';
	import { get } from 'svelte/store';
	import { apiRootUrl } from '$lib/stores.svelte.js';
	import { initLocale, t, translateInLang } from '$lib/i18n.svelte.js';
	import { page } from '$app/state';
	import { onMount } from 'svelte';

	// This component can be shown in an uncloseable modal by using:
	// The fourth parameter (uncloseable=true) prevents the modal from being closed

	let lang = $state(page?.params?.lang || 'fr');

	onMount(() => {
		initLocale();
		
		// Check for pending invitation and prefill email if available
		const pendingInvite = localStorage.getItem('pendingOrgInvite');
		if (pendingInvite) {
			try {
				const invite = JSON.parse(pendingInvite);
				if (invite && typeof invite.email === 'string' && invite.email.trim()) {
					userMail = invite.email.trim();
				}
			} catch (error) {
				console.error('Error parsing pending invite data:', error);
				// Don't remove the invite data here - let the main component handle it
			}
		}
	});

	let userMail = $state(''), emailSignUpSucceeded = $state(false), errorMessage = $state(''), loading = $state(false);

	const knownProviders = [
		{ domain: 'gmail.com',       name: 'Gmail',      url: 'https://mail.google.com/',          logo: '/logos/gmail.svg' },
		{ domain: 'googlemail.com',  name: 'Gmail',      url: 'https://mail.google.com/',          logo: '/logos/gmail.svg' },
		{ domain: 'outlook.com',     name: 'Outlook',    url: 'https://outlook.live.com/mail/0/',  logo: '/logos/outlook.svg' },
		{ domain: 'outlook.fr',     name: 'Outlook',    url: 'https://outlook.live.com/mail/0/',  logo: '/logos/outlook.svg' },
		{ domain: 'hotmail.com',     name: 'Outlook',    url: 'https://outlook.live.com/mail/0/',  logo: '/logos/outlook.svg' },
		{ domain: 'live.com',        name: 'Outlook',    url: 'https://outlook.live.com/mail/0/',  logo: '/logos/outlook.svg' },
		{ domain: 'yahoo.com',       name: 'Yahoo',      url: 'https://mail.yahoo.com',            logo: '/icons/v2/mail-01-stroke-rounded.svg' },
		{ domain: 'ymail.com',       name: 'Yahoo',      url: 'https://mail.yahoo.com',            logo: '/icons/v2/mail-01-stroke-rounded.svg' },
		{ domain: 'icloud.com',      name: 'iCloud',     url: 'https://www.icloud.com/mail',       logo: '/icons/v2/mail-01-stroke-rounded.svg' },
		{ domain: 'me.com',          name: 'iCloud',     url: 'https://www.icloud.com/mail',       logo: '/icons/v2/mail-01-stroke-rounded.svg' },
		{ domain: 'aol.com',         name: 'AOL',        url: 'https://mail.aol.com',              logo: '/icons/v2/mail-01-stroke-rounded.svg' },
		{ domain: 'proton.me',       name: 'ProtonMail', url: 'https://account.proton.me/login',   logo: '/icons/v2/mail-01-stroke-rounded.svg' },
		{ domain: 'protonmail.com',  name: 'ProtonMail', url: 'https://account.proton.me/login',   logo: '/icons/v2/mail-01-stroke-rounded.svg' },
		{ domain: 'gmx.com',         name: 'GMX',        url: 'https://www.gmx.com/mail',          logo: '/icons/v2/mail-01-stroke-rounded.svg' },
		{ domain: 'hey.com',         name: 'HEY',        url: 'https://app.hey.com',               logo: '/icons/v2/mail-01-stroke-rounded.svg' }
	];

	const fallbackProviders = [
		{ name: 'Gmail',   url: 'https://mail.google.com/',          logo: '/logos/gmail.svg' },
		{ name: 'Outlook', url: 'https://outlook.live.com/mail/0/',  logo: '/logos/outlook.svg' },
	];

	const fallbackIcon = '/icons/v2/mail-01-stroke-rounded.svg';

	function validEmail(e: string) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e); }
	let domain = $derived(validEmail(userMail) ? userMail.split('@')[1].toLowerCase() : null);

	async function signUp() {
		if (loading || !validEmail(userMail)) {
			toastDisplay(translateInLang('error.errorEmailNotValid', lang), 'error');
			return;
		}
		try { 
			loading = true; 
			await requestMagicLink(userMail);
			emailSignUpSucceeded = true;
		} catch(e) { errorMessage = translateInLang('error.generalErrorTryLaterMsg', lang); }
		finally { loading = false; }
	}

	function getProviderList() {
		if (!domain) return fallbackProviders;
		const matched = knownProviders.filter(p => p.domain === domain);
		return matched.length ? matched : fallbackProviders;
	}
</script>

<div role="dialog" class="modal" in:fly|global={{ y: 50, duration: 300 }} out:fly|global={{ y: 50, duration: 300 }}>
	<div class="z-40 mx-4 w-full max-w-[32rem] flex flex-col gap-6 p-8 md:p-10 rounded-2xl bg-white dark:bg-ord-cardcolor-dark dark:text-white pointer-events-auto shadow-lg">
		{#if !emailSignUpSucceeded}
			<h2 class="text-center text-3xl md:text-[2.7rem]">
				{translateInLang('user.loginToOrdalie', lang)}
			</h2>
			<div class="flex flex-col gap-3">
				<p>{translateInLang('user.signupMsg1', lang)} <i>Ordalie</i>. {translateInLang('user.signupMsg2', lang)}</p>
				<p>
					{translateInLang('user.signupMsg3', lang)}
					<a class="underline" href="/{page.params.lang}/legal/cgu" target="_blank">{translateInLang('user.signupMsg4', lang)}</a>
					{translateInLang('user.signupMsg5', lang)}
					<a class="underline" href="/{page.params.lang}/legal/privacy" target="_blank">{translateInLang('user.signupMsg6', lang)}</a>.
				</p>
			</div>
			<div class="flex flex-col items-center gap-2">
				<button onclick={() => (window.location.href = `${get(apiRootUrl)}/v3/auth/google/`)} class="ord-button flex w-full h-14 gap-4 items-center justify-center border bg-ord-grey p-3 dark:bg-ord-darkgrey border-ord-border dark:border-ord-border-dark rounded-xl">
					<img class="h-full" src="/logos/google.svg" alt="Google" />
					<span>{translateInLang('user.loginWithGoogle', lang)}</span>
				</button>
				<button onclick={() => (window.location.href = `${get(apiRootUrl)}/v3/auth/microsoft/`)} class="ord-button flex w-full h-14 gap-4 items-center justify-center border bg-ord-grey p-3 dark:bg-ord-darkgrey border-ord-border dark:border-ord-border-dark rounded-xl">
					<img class="h-full" src="/logos/microsoft.svg" alt="Microsoft" />
					<span>{translateInLang('user.loginWithMicrosoft', lang)}</span>
				</button>
				<div>{translateInLang('info.or', lang)}</div>
				<div class="flex w-full h-14 items-center border rounded-xl border-ord-border dark:border-ord-border-dark">
					<input bind:value={userMail} onkeydown={(e) => e.key === 'Enter' && signUp()} class="flex-1 p-3 bg-transparent text-black dark:text-white" type="email" placeholder="bruce@wayne.com" />
					{#if !loading}
						<button onclick={signUp} class="px-4 py-2">
							<img class="h-5 rotate-180" src="/icons/v2/arrow-left-02-stroke-rounded.svg" alt="Go" />
						</button>
					{:else}
						<div class="px-4"><Spinner size={5} /></div>
					{/if}
				</div>
				{#if errorMessage}<div class="text-red-500">{errorMessage}</div>{/if}
			</div>
		{:else}
			<h2 class="text-3xl">{translateInLang('user.almostThere', lang)}</h2>
			<p>{translateInLang('user.almostThereMsg1', lang)} <span class="opacity-50">({translateInLang('user.almostThereMsg2', lang)})</span>.</p>
			<div class="flex flex-col gap-2">
				{#each getProviderList() as provider}
					<a href={provider.url} target="_blank" class="flex items-center gap-2 border p-3 rounded-xl border-ord-border dark:border-ord-border-dark">
						<img src={provider.logo || fallbackIcon} class="w-5 dark:invert" alt={provider.name} />
						<span class="font-medium">{translateInLang('user.open', lang)} {provider.name}</span>
					</a>
				{/each}
			</div>
		{/if}
	</div>
</div>

<style>
	.modal {
		position: fixed; inset: 0;
		display: flex; align-items: center; justify-content: center;
		pointer-events: none;
	}
</style>